import type { CurrentUser } from "@/lib/auth";
import {
  fetchMeGroups,
  userHasGroup,
  userIsBuero,
  userIsMonteure,
  userIsRealAppAdmin,
} from "@/lib/auth";

let cachedGroups: string[] | null = null;
let groupsPromise: Promise<string[]> | null = null;

export async function ensureOverviewGroups(user: CurrentUser | null): Promise<string[]> {
  if (cachedGroups) return cachedGroups;
  const fromUser = (user?.groups as string[] | undefined) ?? [];
  if (fromUser.length) {
    cachedGroups = fromUser;
    return cachedGroups;
  }
  if (!groupsPromise) {
    groupsPromise = fetchMeGroups()
      .then((g) => {
        cachedGroups = g;
        return g;
      })
      .catch(() => {
        groupsPromise = null;
        return [] as string[];
      });
  }
  return groupsPromise;
}

/** Büro oder Monteurs-Gruppe (Frontend-Checks analog zu Backend `user_is_monteure_member`). */
export function userIsMontage(user: CurrentUser | null, groups: string[]): boolean {
  if (userIsMonteure(user)) return true;
  if (userHasGroup(user, "Montage")) return true;
  const combined = [...(user?.groups || []), ...groups];
  return combined.some((g) => {
    const n = String(g).toLowerCase();
    return n.includes("montage") || n.includes("monteur") || n.includes("monteure");
  });
}

export function userCanManageHeroFieldsInOverview(
  user: CurrentUser | null,
  groups: string[],
): boolean {
  return userIsBuero(user) || userIsMontage(user, groups);
}

export function userCanCreateTimesheetsFromOverview(
  user: CurrentUser | null,
  groups: string[],
): boolean {
  if (userIsBuero(user)) return true;
  return groups.some((g) => {
    const n = String(g).toLowerCase();
    return n.includes("monteur") || n.includes("montage");
  });
}

/** Bestellungen, App-Dokumente & Hero-Dokumente – nur Büro, wie in auftraege.js. */
export function userCanViewBueroProjectOverviewSections(user: CurrentUser | null): boolean {
  return userIsBuero(user);
}

/** Projektfotos ansehen und hochladen – Büro, App-Admins und Monteure. */
export function userCanViewProjectFotos(
  user: CurrentUser | null,
  groups: string[] = [],
): boolean {
  return userIsBuero(user) || userIsRealAppAdmin(user) || userIsMontage(user, groups);
}

/** QR-Aushang für neue Materialanforderungen – Büro und App-Admins. */
export function userCanCreateMaterialRequestQr(user: CurrentUser | null): boolean {
  return userIsBuero(user) || userIsRealAppAdmin(user);
}

/** Bestellungen aus der Projektübersicht – Büro und App-Admins. */
export function userCanCreateOrdersFromOverview(user: CurrentUser | null): boolean {
  return userIsBuero(user) || userIsRealAppAdmin(user);
}

/** Materialanforderungen aus der Projektübersicht – Büro, Admins und Monteure. */
export function userCanCreateMaterialRequestsFromOverview(
  user: CurrentUser | null,
  groups: string[],
): boolean {
  if (userIsBuero(user) || userIsRealAppAdmin(user)) return true;
  return userIsMontage(user, groups);
}

/** Wartungsobjekte/Türen am Auftrag verknüpfen (Büro + Monteure). */
export function userCanManageLinkedAssets(
  user: CurrentUser | null,
  groups: string[],
): boolean {
  return userIsBuero(user) || userIsMontage(user, groups) || userIsRealAppAdmin(user);
}

/** Montageunterlagen anzeigen (Büro + Monteure). */
export function userCanViewMontageunterlagen(
  user: CurrentUser | null,
  groups: string[],
): boolean {
  return userIsBuero(user) || userIsMontage(user, groups);
}

/** Vollständige Projektseite aus der Übersicht – Büro und App-Admins. */
export function userCanOpenProjectPage(user: CurrentUser | null): boolean {
  return userIsBuero(user) || userIsRealAppAdmin(user);
}

/** Montageunterlagen anlegen/bearbeiten/löschen – nur Büro/App-Admins. */
export function userCanManageMontageunterlagen(
  user: CurrentUser | null,
  _groups: string[] = [],
): boolean {
  return userIsBuero(user) || userIsRealAppAdmin(user);
}
